import type { SxProps } from "@mui/system";
import type { ThreadMessage, ConceptVersion, EditMeta } from "./types";
import type { ChatAttachedMaterial, ChatAttachedImage } from "./Chat/QuickEdit";
import type { Segment, SegmentData } from "../imageEditor/spatialIndex";

/** Raw data stored at segments_url — supports both old (SAM3-only) and new (product finder) formats */
interface SegmentCacheData {
  image_width: number;
  image_height: number;
  segments: Array<Record<string, unknown>>;
  collapsed_surfaces?: Array<Record<string, unknown>>;
  matching_products?: Array<Record<string, unknown>>;
  external_products?: { external_images: string[]; mattoboard_products: string[] };
}

export interface CollapsedSurface {
  id: string;
  label: string;
  slot: string;
  bbox: number[];
  source_segment_ids: string[];
}

/** One commercial (MattoLens) product, verbatim from product_finder's
 *  per-segment `seg.mattolens` (present when the backend asked mattolens=true). */
export interface MattoLensItem {
  id?: string;
  product_id?: string;
  name?: string;
  supplier?: string;
  image_url?: string;
  product_link?: string;
  application?: string;
  product_type?: string;
  match_score?: number;
}

/** A surface's commercial matches, ready to render in the Matched Products tab. */
export interface MattoLensSlot {
  segment_id: string;
  slot_name: string;
  products: MattoLensItem[];
  /** Held from the previous image during an edit recompute — see
   *  productMatching.applyStaleSlotRetention (segment_id is severed). */
  stale?: boolean;
}

/** Parsed result with SpatialIndex-compatible segments + optional products */
export interface ParsedSegmentCache {
  segmentData: SegmentData;
  /** Collapsed surface lookup keyed by surface id (e.g. "cabinetry_1") */
  collapsedSurfaceMap: Map<string, CollapsedSurface> | null;
  matchingProducts: Array<Record<string, unknown>> | null;
  externalProducts: { external_images: string[]; mattoboard_products: string[] } | null;
  /** Per-surface commercial (MattoLens) matches, synthesized from each
   *  segment's `mattolens` array. Empty when product_finder ran without
   *  mattolens=true (e.g. a concept cached before this was enabled). */
  mattolensSlots: MattoLensSlot[];
}

/**
 * Parse segments_url JSON, handling both old (SAM3-only) and new (product finder) formats.
 * Filters to segments that have all required fields for SpatialIndex.
 */
export function parseSegmentCacheData(raw: unknown): ParsedSegmentCache {
  const data = raw as SegmentCacheData;

  if (!data || typeof data !== "object" || !Array.isArray(data.segments)) {
    throw new Error("Invalid segment cache data: missing or invalid segments array");
  }
  if (!data.image_width || !data.image_height) {
    throw new Error("Invalid segment cache data: missing image dimensions");
  }

  const filterValid = (arr: Array<Record<string, unknown>>) =>
    arr.filter((s) => {
      if ("status" in s && s.status !== "matched") return false;
      return s.score != null && s.bbox != null && s.polygon_flat != null && s.area != null;
    });

  const validSegments = filterValid(data.segments);

  if (validSegments.length === 0 && data.segments.length > 0) {
    console.warn(
      `[parseSegmentCacheData] All ${data.segments.length} segments were filtered out ` +
        `(excluded/dropped/missing fields). No clickable regions available.`,
    );
  }

  // Build collapsed surface lookup when available (new product finder format)
  let collapsedSurfaceMap: Map<string, CollapsedSurface> | null = null;
  if (Array.isArray(data.collapsed_surfaces) && data.collapsed_surfaces.length > 0) {
    collapsedSurfaceMap = new Map();
    for (const cs of data.collapsed_surfaces) {
      const id = cs.id as string;
      if (id) {
        collapsedSurfaceMap.set(id, {
          id,
          label: (cs.label as string) ?? id,
          slot: (cs.slot as string) ?? "",
          bbox: (cs.bbox as number[]) ?? [],
          source_segment_ids: (cs.source_segment_ids as string[]) ?? [],
        });
      }
    }
  }

  // Merge collapsed surfaces that have no matching raw segment into the segment
  // list so they are hoverable. Raw segments have accurate polygons; collapsed
  // surfaces only have the representative polygon but are better than nothing.
  let allSegments = validSegments;
  if (collapsedSurfaceMap) {
    const coveredIds = new Set(validSegments.map((s) => (s as Record<string, unknown>).surface_id));
    const extraFromCollapsed = filterValid(
      data.collapsed_surfaces!.filter((cs) => !coveredIds.has(cs.id)),
    );
    // Tag each collapsed surface with its own id as surface_id so the lookup works
    for (const cs of extraFromCollapsed) {
      if (!cs.surface_id) cs.surface_id = cs.id;
    }
    allSegments = [...validSegments, ...extraFromCollapsed];
  }

  // Commercial (MattoLens) matches — synthesized per segment from `seg.mattolens`.
  // Each matched object segment may carry a list of buyable products; turn each
  // into a slot labeled by the segment's classified type (e.g. "Mirror", "Sofa").
  const mattolensSlots: MattoLensSlot[] = [];
  for (const seg of data.segments) {
    const ml = seg.mattolens;
    if (!Array.isArray(ml) || ml.length === 0) continue;
    const segId = String(seg.id ?? "");
    const slotName = String(
      seg.mattolens_type ?? seg.sam3_label ?? seg.label ?? seg.application_type ?? segId,
    );
    mattolensSlots.push({
      segment_id: segId,
      slot_name: slotName,
      products: ml as MattoLensItem[],
    });
  }

  return {
    segmentData: {
      image_width: data.image_width,
      image_height: data.image_height,
      segments: allSegments as unknown as Segment[],
    },
    collapsedSurfaceMap,
    matchingProducts: data.matching_products ?? null,
    externalProducts: data.external_products ?? null,
    mattolensSlots,
  };
}

/** Allowed MIME types for image attachments */
export const ALLOWED_IMAGE_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/avif",
]);

/** Maximum file size for image attachments (20 MB) */
export const MAX_IMAGE_SIZE_BYTES = 20 * 1024 * 1024;

/**
 * Gradient for primary action buttons.
 */
export const GRADIENT =
  "linear-gradient(89.95deg, #5B47F5 1.57%, #B782FF 50.91%, #D0B1FE 98.39%)";

/**
 * Darker variant of {@link GRADIENT} for the hover state of primary action
 * buttons. Matches the Prompt Builder gradient hover on /home/concepts/new.
 */
export const GRADIENT_HOVER =
  "linear-gradient(89.95deg, #4A38D9 1.57%, #9F6FE6 50.91%, #C09FED 98.39%)";

/**
 * Validate image type and emptiness (fast, synchronous checks before compression).
 * Returns null if valid, or an error message string if invalid.
 */
export const validateImageType = (file: File): string | null => {
  if (!ALLOWED_IMAGE_TYPES.has(file.type)) {
    return "Only JPG, PNG, WebP, and AVIF images are supported";
  }
  if (file.size === 0) {
    return "Image file is empty. Please try again.";
  }
  return null;
};

/**
 * Validate an image file for attachment (type, emptiness, and size).
 * Returns null if valid, or an error message string if invalid.
 */
export const validateImageFile = (file: File): string | null => {
  const typeError = validateImageType(file);
  if (typeError) return typeError;
  if (file.size > MAX_IMAGE_SIZE_BYTES) {
    return `Image must be smaller than ${MAX_IMAGE_SIZE_BYTES / (1024 * 1024)} MB`;
  }
  return null;
};

/**
 * Resolve the analytics "kind" of an attached material.
 * Returns null when no material is attached.
 */
export const getMaterialKind = (
  material?: ChatAttachedMaterial,
): "paint" | "fixture" | "material" | null => {
  if (!material) return null;
  if (isMaterialPaint(material)) return "paint";
  if (isMaterialFixture(material)) return "fixture";
  return "material";
};

/**
 * Read the analytics fields off an attached material in one place, so the
 * per-block builder and the edit_meta summary stay in sync.
 */
export const getMaterialMetaFields = (material?: ChatAttachedMaterial) => ({
  is_paint: isMaterialPaint(material),
  is_fixture: isMaterialFixture(material),
  paint_finish: material?.paintFinish ?? null,
  custom_repeat:
    typeof material?.customRepeat === "number" ? material.customRepeat : null,
});

/**
 * The image's real source/use tag (Material/Object/Other or a
 * REFERENCE_IMAGE_TAG); falls back to "reference" when no category is known.
 */
export const getImageUseCategory = (image?: ChatAttachedImage): string =>
  image?.category || "reference";

/** Map the editor's editMode to the analytics targeting tool. */
const EDIT_MODE_TO_TOOL: Record<
  "paint" | "collage" | "combined",
  EditMeta["tool"]
> = { paint: "paint", collage: "collage", combined: "combined" };

export const editModeToTool = (
  editMode?: "paint" | "collage" | "combined",
): EditMeta["tool"] => (editMode ? EDIT_MODE_TO_TOOL[editMode] : "whole_image");

/**
 * Build a message content array from text and optional attachments.
 * Used when persisting user messages to Firestore.
 *
 * The edit box also carries per-block material/image fields the schema declares
 * (custom_repeat / paint_finish / is_paint / is_fixture, and the image's real
 * use/category tag) so each block stays self-describing.
 */
export const buildMessageContent = (
  text: string,
  material?: ChatAttachedMaterial,
  referenceImage?: ChatAttachedImage,
): any[] => {
  const content: any[] = [{ type: "input_text" as const, text }];

  if (material?.renderedImage) {
    if (material.id) {
      const meta = getMaterialMetaFields(material);
      content.push({
        type: "input_material" as const,
        material_id: material.id,
        image_url: material.renderedImage,
        name: material.name,
        // Per-block fields (additive). Only set when truthy so old readers and
        // the discriminated-union validator are unaffected.
        ...(meta.is_paint ? { is_paint: true } : {}),
        ...(meta.is_fixture ? { is_fixture: true } : {}),
        ...(meta.paint_finish ? { paint_finish: meta.paint_finish } : {}),
        ...(meta.custom_repeat !== null
          ? { custom_repeat: meta.custom_repeat }
          : {}),
      });
    } else {
      content.push({ type: "input_image" as const, image_url: material.renderedImage });
    }
  }

  if (referenceImage?.uploadUrl) {
    content.push({
      type: "input_image" as const,
      image_url: referenceImage.uploadUrl,
      use: getImageUseCategory(referenceImage),
    });
  }

  return content;
};

/**
 * Assemble the denormalized `edit_meta` analytics summary stamped on the user
 * message created from the edit box. This is the PRIMARY analytics surface —
 * one object, no per-block walking. See `EditMetaSchema` in @mattoboard/schemas.
 */
export const buildEditMeta = (
  text: string,
  material: ChatAttachedMaterial | undefined,
  referenceImage: ChatAttachedImage | undefined,
  opts: { tool: EditMeta["tool"]; segmentLabel?: string | null },
): EditMeta => {
  const meta = getMaterialMetaFields(material);
  const imageUses = referenceImage?.uploadUrl
    ? [getImageUseCategory(referenceImage)]
    : [];
  return {
    tool: opts.tool,
    segment_label: opts.segmentLabel ?? null,
    has_material: !!material,
    material_id: material?.id ?? null,
    material_kind: getMaterialKind(material),
    custom_repeat: meta.custom_repeat,
    paint_finish: meta.paint_finish,
    image_count: imageUses.length,
    image_uses: imageUses,
    text_len: text.length,
  };
};

/**
 * Format seconds into a human-readable duration like "8s" or "1m 23s"
 */
export const formatDuration = (seconds: number): string => {
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
};

/**
 * Extract text from a ThreadMessage's content blocks.
 */
export const getMessageText = (msg: ThreadMessage): string => {
  if (typeof msg.content === "string") return msg.content;
  if (Array.isArray(msg.content)) {
    const textParts = (msg.content as any[])
      .filter(
        (b) =>
          (b.type === "input_text" ||
            b.type === "output_text" ||
            b.type === "text") &&
          typeof b.text === "string" &&
          b.text.trim().length > 0,
      )
      .map((b) => b.text.trim());
    return textParts.join("\n");
  }
  if (typeof msg.content === "object" && msg.content !== null) {
    const content = msg.content as any;
    if (content.text) return content.text;
  }
  return "";
};

/**
 * Extract image URLs from a ThreadMessage's content blocks.
 */
export const getMessageImages = (msg: ThreadMessage): string[] => {
  const images: string[] = [];
  if (Array.isArray(msg.content)) {
    for (const block of msg.content as any[]) {
      if (block.type === "input_image" && block.image_url) {
        images.push(block.image_url);
      }
    }
  }
  return images;
};

export interface MessageMaterial {
  material_id: string;
  image_url?: string;
  name?: string;
}

/**
 * Extract attached materials from a ThreadMessage's content blocks.
 */
export const getMessageMaterials = (msg: ThreadMessage): MessageMaterial[] => {
  const materials: MessageMaterial[] = [];
  if (Array.isArray(msg.content)) {
    for (const block of msg.content as any[]) {
      if (block.type === "input_material" && block.material_id && block.image_url) {
        materials.push({
          material_id: block.material_id,
          image_url: block.image_url,
          name: block.name,
        });
      }
    }
  }
  return materials;
};

/**
 * Copy version metadata fields from a source version.
 * Returns an object with only the metadata fields, suitable for spreading into a new version.
 */
export const copyVersionMetadata = (
  source: ConceptVersion | null | undefined,
): Partial<ConceptVersion> => {
  if (!source) return {};
  return {
    name: source.name || undefined,
    narrative: source.narrative || undefined,
    location: source.location || undefined,
    budget: source.budget || undefined,
    space: source.space || undefined,
    tag: source.tag || undefined,
    style: source.style || undefined,
    palette: source.palette || undefined,
    segments_url: source.segments_url || undefined,
    collection_id: source.collection_id || undefined,
    project_id: source.project_id || undefined,
    region: source.region || undefined,
  };
};

/**
 * Download an image by URL.
 * Creates a temporary blob URL and triggers a download.
 */
export const downloadImage = async (
  imageUrl: string,
  filename: string,
  onError?: (message: string) => void,
): Promise<boolean> => {
  if (!imageUrl) {
    onError?.("No image available to download");
    return false;
  }
  try {
    const response = await fetch(imageUrl);
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const sanitizedFilename = filename.replace(/[^a-zA-Z0-9]/g, "");
    link.download = `${sanitizedFilename || "concept"}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    return true;
  } catch {
    onError?.("Failed to download image. Please try again.");
    return false;
  }
};

/**
 * Shared button styles for secondary action buttons.
 */
export const secondaryButtonStyles: SxProps = {
  textTransform: "none",
  borderRadius: 2,
  px: 2,
  py: 1,
  fontSize: "0.85rem",
  fontWeight: 500,
  boxShadow: "0 2px 6px rgba(0, 0, 0, 0.12)",
  backgroundColor: "#fff",
  color: "#333",
  border: "1px solid #c7c7c7",
  "&:hover": {
    backgroundColor: "#f5f5f5",
    borderColor: "#bdbdbd",
  },
};

/**
 * Pill-shaped action bar container.
 */
export const actionBarContainerStyles: SxProps = {
  position: "relative",
  zIndex: 2,
  display: "inline-flex",
  alignItems: "center",
  gap: 0.5,
  backgroundColor: "#ffffff",
  borderRadius: "12px",
  px: 1,
  py: 0.5,
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
};

/**
 * Primary gradient CTA button inside the action bar.
 */
export const primaryActionButtonStyles: SxProps = {
  textTransform: "none",
  borderRadius: "12px",
  px: 1.5,
  py: 0.75,
  fontSize: "0.85rem",
  fontWeight: 600,
  color: "#333",
  backgroundColor: "transparent",
  boxShadow: "none",
  whiteSpace: "nowrap",
  "&:hover": {
    background:
      GRADIENT,
    color: "#fff",
    boxShadow: "0 4px 12px rgba(91, 71, 245, 0.3)",
  },
};

/**
 * Icon button styles for the action bar. Accepts active state for toggle buttons.
 */
export const actionIconButtonStyles = (isActive: boolean): SxProps => ({
  width: 36,
  height: 36,
  borderRadius: "8px",
  color: isActive ? "#5B47F5" : "#666",
  backgroundColor: isActive ? "rgba(91, 71, 245, 0.12)" : "transparent",
  "&:hover": {
    backgroundColor: isActive
      ? "rgba(91, 71, 245, 0.18)"
      : "rgba(0, 0, 0, 0.06)",
  },
});

/**
 * Return a user-friendly error message for connection errors, pass through others.
 */
export function getConnectionErrorMessage(err: unknown): string {
  const raw = err instanceof Error ? err.message : String(err);
  if (/Failed to fetch|NetworkError|ERR_CONNECTION|ECONNREFUSED/i.test(raw)) {
    return "Cannot connect to server. Please try again.";
  }
  return raw || "Something went wrong. Please try again.";
}

/**
 * Check if a material is a paint type.
 */
export const isMaterialPaint = (material: any): boolean =>
  material?.productType === "paint" || material?.type === "paint";

/**
 * Check if a material is a fixture (hardware/accessory).
 */
export const isMaterialFixture = (material: any): boolean =>
  material?.isFixture ||
  material?.productType === "hardware" ||
  material?.productType === "accessory";

/**
 * Check if a material drag payload is a user-uploaded image.
 * User uploads have `files` as a direct URL string, not an object with texture maps.
 */
export const isUserUpload = (material: any): boolean =>
  typeof material?.files === "string" &&
  !material?.materialData &&
  !material?.paintMaterialsData;

export type SendToChatAction = "prefillImage" | "prefillMaterial";

export const resolveSendToChatAction = (product: any): SendToChatAction => {
  const cat = product?.category;
  if (typeof cat === "string" && cat !== "Material") return "prefillImage";
  return "prefillMaterial";
};

/**
 * Action types that SHOULD show result thumbnails in the chat sidebar.
 * Only initial creation shows an inline image; edits/modifications are text-only
 * in chat (the result image lives in the version history).
 */
const THUMBNAIL_ACTIONS = new Set(["LLM_GENERATION"]);

/**
 * Build a Map of messageId → version for showing thumbnails in chat bubbles.
 * Only includes versions whose action_type is in the allowlist.
 */
export const buildVersionByMessageId = (
  versions: ConceptVersion[],
): Map<string, ConceptVersion> => {
  const map = new Map<string, ConceptVersion>();
  versions.forEach((v) => {
    if (v.source_message_id && v.img_url && THUMBNAIL_ACTIONS.has(v.action_type || "")) {
      map.set(v.source_message_id, v);
    }
  });
  return map;
};

/** Read-only text from the HTML the concept header editor stores (#4106). */
export const htmlToPlainText = (html: string | null | undefined): string => {
  if (!html) return "";
  const prepared = html
    .replace(/<br\s*\/?>/gi, "\n")
    // A "<" that opens no complete tag is plain text (AI-written values).
    .replace(/<(?![^<>]*>)/g, "&lt;");
  const text =
    new DOMParser().parseFromString(prepared, "text/html").documentElement
      .textContent ?? "";
  return text.replace(/\u00a0/g, " ").trim();
};

/** Cut editor HTML to maxChars without leaving a partial entity or tag. */
export const truncateEditorHtml = (html: string, maxChars: number): string =>
  html.slice(0, maxChars).replace(/&[a-z\d#]*$|<[^>]*$/i, "");
